import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type RefObject,
} from 'react';

import type { SelectizeOption } from '@/types/forms';

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 1;

// =============================================================================
// TYPES
// =============================================================================

export type DropdownBehavior = 'close' | 'reposition';

export interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

export interface UseSelectizeOptions<
  T extends SelectizeOption = SelectizeOption,
> {
  /** Static options array */
  options?: T[] | undefined;
  /** Async function to load options remotely */
  loadOptions?: ((query: string) => Promise<T[]>) | undefined;
  /** Controlled selected value(s) */
  value?: T | T[] | null | undefined;
  /** Callback when selection changes */
  onChange?: ((value: T | T[] | null) => void) | undefined;
  /** Allow multiple selections */
  multiple?: boolean | undefined;
  /** Allow creating new options from input */
  creatable?: boolean | undefined;
  /** Factory function to create new option from input */
  createOption?: ((inputValue: string) => T) | undefined;
  /** Debounce delay for remote loading (ms) */
  debounceMs?: number | undefined;
  /** Minimum characters before searching */
  minSearchLength?: number | undefined;
  /** Whether the selectize is disabled */
  disabled?: boolean | undefined;
  /** Custom filter function for local options */
  filterOption?: ((option: T, query: string) => boolean) | undefined;
  /** Close dropdown on selection (default: true for single, false for multi) */
  closeOnSelect?: boolean | undefined;
  /** Maximum number of selections (multi mode) */
  maxSelections?: number | undefined;
  /**
   * Dropdown behaviour on scroll/resize.
   *
   * Both modes reposition while the control is on screen — a dropdown that
   * closed on any scroll made the component unusable inside a modal or bottom
   * sheet, where the body scrolls under it.
   *
   * - 'close': also dismiss once the control scrolls out of view (default)
   * - 'reposition': follow the control regardless (default for creatable)
   *
   * Neither clears the query. Losing what someone typed to an incidental
   * scroll is worse than a briefly mispositioned dropdown.
   */
  dropdownBehavior?: DropdownBehavior | undefined;
}

export interface UseSelectizeReturn<
  T extends SelectizeOption = SelectizeOption,
> {
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Open the dropdown */
  open: () => void;
  /** Close the dropdown */
  close: () => void;
  /** Toggle the dropdown */
  toggle: () => void;
  /** Current search query */
  query: string;
  /** Set the search query */
  setQuery: (query: string) => void;
  /** Filtered/loaded options to display */
  filteredOptions: T[];
  /** Currently selected value(s) */
  selectedValues: T[];
  /** Index of highlighted option in dropdown */
  highlightedIndex: number;
  /** Set highlighted index */
  setHighlightedIndex: (index: number) => void;
  /** Whether remote options are loading */
  isLoading: boolean;
  /** Error from remote loading */
  error: string | null;
  /** Select an option */
  selectOption: (option: T) => void;
  /** Remove a selected option (multi mode) */
  removeOption: (option: T) => void;
  /** Clear all selections */
  clearAll: () => void;
  /** Handle keyboard events */
  handleKeyDown: (event: React.KeyboardEvent) => void;
  /** Handle input change */
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handle input focus */
  handleInputFocus: () => void;
  /** Handle input blur */
  handleInputBlur: () => void;
  /** Ref for the container element */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Ref for the input element */
  inputRef: RefObject<HTMLInputElement | null>;
  /** Ref for the dropdown element */
  dropdownRef: RefObject<HTMLDivElement | null>;
  /** Whether max selections reached (multi mode) */
  isMaxReached: boolean;
  /** Whether a new option can be created from current query */
  canCreate: boolean;
  /** Create option from current query */
  createFromQuery: () => void;
  /** Dropdown position for portal rendering */
  dropdownPosition: DropdownPosition | null;
}

// =============================================================================
// DEFAULT FILTER
// =============================================================================

function defaultFilterOption<T extends SelectizeOption>(
  option: T,
  query: string
): boolean {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return true;

  return (
    option.label.toLowerCase().includes(lowerQuery) ||
    option.value.toLowerCase().includes(lowerQuery) ||
    (option.subtitle?.toLowerCase().includes(lowerQuery) ?? false)
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useSelectize<T extends SelectizeOption = SelectizeOption>(
  options: UseSelectizeOptions<T> = {}
): UseSelectizeReturn<T> {
  const {
    options: staticOptions = [],
    loadOptions,
    value,
    onChange,
    multiple = false,
    creatable = false,
    createOption,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minSearchLength = MIN_SEARCH_LENGTH,
    disabled = false,
    filterOption = defaultFilterOption,
    closeOnSelect,
    maxSelections,
    dropdownBehavior,
  } = options;

  // Determine close behavior
  const shouldCloseOnSelect = closeOnSelect ?? !multiple;

  // Determine scroll/resize behavior (default: reposition for creatable, close otherwise)
  const effectiveBehavior: DropdownBehavior =
    dropdownBehavior ?? (creatable ? 'reposition' : 'close');

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [remoteOptions, setRemoteOptions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortControllerRef = useRef<AbortController>(undefined);

  // Calculate dropdown position from container's bounding rect
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  }, []);

  // Normalize selected values to array
  const selectedValues = useMemo((): T[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
  }, [value]);

  // Check if max selections reached
  const isMaxReached = useMemo(() => {
    if (!multiple || !maxSelections) return false;
    return selectedValues.length >= maxSelections;
  }, [multiple, maxSelections, selectedValues.length]);

  // Filter static options or use remote options
  const filteredOptions = useMemo((): T[] => {
    const baseOptions = loadOptions ? remoteOptions : staticOptions;

    // Filter out already selected options in multi mode
    const availableOptions = multiple
      ? baseOptions.filter(
          (opt) => !selectedValues.some((sel) => sel.value === opt.value)
        )
      : baseOptions;

    // Apply local filter if not using remote
    if (loadOptions) {
      return availableOptions;
    }

    return availableOptions.filter((opt) => filterOption(opt, query));
  }, [
    loadOptions,
    remoteOptions,
    staticOptions,
    multiple,
    selectedValues,
    filterOption,
    query,
  ]);

  // Check if can create new option
  const canCreate = useMemo(() => {
    if (!creatable || !query.trim()) return false;
    if (isMaxReached) return false;

    // Check if query matches existing option
    const queryLower = query.toLowerCase().trim();
    const existsInOptions = [...staticOptions, ...remoteOptions].some(
      (opt) =>
        opt.label.toLowerCase() === queryLower ||
        opt.value.toLowerCase() === queryLower
    );
    const existsInSelected = selectedValues.some(
      (opt) =>
        opt.label.toLowerCase() === queryLower ||
        opt.value.toLowerCase() === queryLower
    );

    return !existsInOptions && !existsInSelected;
  }, [
    creatable,
    query,
    isMaxReached,
    staticOptions,
    remoteOptions,
    selectedValues,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  // Load remote options when query changes
  useEffect(() => {
    if (!loadOptions || !isOpen) return;

    // Clear timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Check min length
    if (query.length < minSearchLength) {
      setRemoteOptions([]);
      setError(null);
      return;
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(async () => {
      // Cancel previous request
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const results = await loadOptions(query);
        setRemoteOptions(results);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message);
          setRemoteOptions([]);
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);
  }, [query, loadOptions, isOpen, minSearchLength, debounceMs]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
  }, [filteredOptions.length]);

  // Click outside handler (checks both container and portaled dropdown)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInContainer = containerRef.current?.contains(target);
      const clickedInDropdown = dropdownRef.current?.contains(target);

      if (!clickedInContainer && !clickedInDropdown) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Position tracking: update on open, handle scroll/resize based on behavior
  useEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }

    // Calculate initial position
    updateDropdownPosition();

    /**
     * Dismiss because the anchor moved out of reach, not because the user asked.
     *
     * Blurs as well as closing: leaving focus on an input whose dropdown has
     * vanished reads as a half-dismissed control — the caret still blinks but
     * typing appears to do nothing. The query is deliberately NOT cleared;
     * that belongs to an explicit dismissal, and wiping what someone typed
     * because the page happened to scroll is the more damaging failure.
     */
    const dismissOutOfView = () => {
      setIsOpen(false);
      inputRef.current?.blur();
    };

    /** True while the control is still on screen and worth pointing at. */
    const isAnchorVisible = (): boolean => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return false;
      return rect.bottom > 0 && rect.top < window.innerHeight;
    };

    const reflow = () => {
      if (effectiveBehavior === 'close' && !isAnchorVisible()) {
        dismissOutOfView();
        return;
      }
      updateDropdownPosition();
    };

    const handleResize = reflow;

    const handleScroll = (event: Event) => {
      // Ignore scroll events from within the dropdown (user scrolling options)
      if (dropdownRef.current?.contains(event.target as Node)) {
        return;
      }
      reflow();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, effectiveBehavior, updateDropdownPosition]);

  // Open/close handlers
  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Selection handlers
  const selectOption = useCallback(
    (option: T) => {
      if (disabled || option.disabled || isMaxReached) return;

      if (multiple) {
        const newValues = [...selectedValues, option];
        onChange?.(newValues);
      } else {
        onChange?.(option);
      }

      if (shouldCloseOnSelect) {
        close();
      } else {
        setQuery('');
        inputRef.current?.focus();
      }
    },
    [
      disabled,
      isMaxReached,
      multiple,
      selectedValues,
      onChange,
      shouldCloseOnSelect,
      close,
    ]
  );

  const removeOption = useCallback(
    (option: T) => {
      if (disabled) return;

      if (multiple) {
        const newValues = selectedValues.filter(
          (sel) => sel.value !== option.value
        );
        onChange?.(newValues);
      } else {
        onChange?.(null);
      }
    },
    [disabled, multiple, selectedValues, onChange]
  );

  const clearAll = useCallback(() => {
    if (disabled) return;
    onChange?.(multiple ? [] : null);
    setQuery('');
  }, [disabled, onChange, multiple]);

  // Create new option
  const createFromQuery = useCallback(() => {
    if (!canCreate) return;

    const trimmedQuery = query.trim();
    const newOption = createOption
      ? createOption(trimmedQuery)
      : ({ value: trimmedQuery, label: trimmedQuery } as T);

    selectOption(newOption);
  }, [canCreate, query, createOption, selectOption]);

  // Input handlers
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = event.target.value;
      setQuery(newQuery);

      if (!isOpen && newQuery) {
        setIsOpen(true);
      }
    },
    [isOpen]
  );

  const handleInputFocus = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  const handleInputBlur = useCallback(() => {
    // Blur handling is done via click-outside detection
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            open();
          } else {
            setHighlightedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0
            );
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1
            );
          }
          break;

        case 'Enter':
          event.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = filteredOptions[highlightedIndex];
            if (option && !option.disabled) {
              selectOption(option);
            }
          } else if (canCreate) {
            createFromQuery();
          }
          break;

        case 'Escape':
          event.preventDefault();
          close();
          break;

        case 'Backspace':
          if (!query && multiple && selectedValues.length > 0) {
            removeOption(selectedValues[selectedValues.length - 1]);
          }
          break;

        case 'Tab':
          if (isOpen) {
            close();
          }
          break;

        case 'Home':
          if (isOpen) {
            event.preventDefault();
            setHighlightedIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            event.preventDefault();
            setHighlightedIndex(filteredOptions.length - 1);
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      open,
      close,
      filteredOptions,
      highlightedIndex,
      selectOption,
      canCreate,
      createFromQuery,
      query,
      multiple,
      selectedValues,
      removeOption,
    ]
  );

  // Scroll highlighted option into view
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0 || !dropdownRef.current) return;

    const highlightedElement = dropdownRef.current.querySelector(
      `[data-index="${highlightedIndex}"]`
    );

    if (highlightedElement) {
      highlightedElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, isOpen]);

  return {
    isOpen,
    open,
    close,
    toggle,
    query,
    setQuery,
    filteredOptions,
    selectedValues,
    highlightedIndex,
    setHighlightedIndex,
    isLoading,
    error,
    selectOption,
    removeOption,
    clearAll,
    handleKeyDown,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    containerRef,
    inputRef,
    dropdownRef,
    isMaxReached,
    canCreate,
    createFromQuery,
    dropdownPosition,
  };
}

export default useSelectize;
